import type {
  DataISO,
  Dipendente,
  Evento,
  ProgrammaSettimanale,
} from '../api/tipi.ts';

/**
 * Trasformazioni pure che stanno dietro al calendario.
 *
 * Sono separate dal componente React perche' sono la parte che vale la pena
 * testare: la disposizione delle righe e l'indicizzazione degli eventi
 * decidono cosa finisce in ogni cella, mentre il resto e' posizionamento.
 */

export type TipoRiga = 'evento' | 'ore' | 'nota' | 'intestazioneGruppo';

export interface RigaGriglia {
  chiave: string;
  tipo: TipoRiga;
  /** Assente solo sulle righe di intestazione di gruppo. */
  dipendente: Dipendente | null;
  /** Etichetta del gruppo, valorizzata solo sulle intestazioni. */
  gruppo: string | null;
  primaDelBlocco: boolean;
  ultimaDelBlocco: boolean;
}

/**
 * Costruisce l'elenco piatto delle righe da virtualizzare.
 *
 * Ogni dipendente occupa tre righe: evento, ore, nota. Nel file Excel per i
 * funzionari la terza veniva riusata per il programma della settimana
 * successiva, ed e' per questo che nel file loro non hanno una seconda nota:
 * qui la riprendono, perche' era un compromesso imposto dal foglio di
 * calcolo, non una scelta.
 *
 * Le righe hanno tutte la stessa altezza, cosi' il virtualizzatore lavora su
 * un elenco uniforme anche se i blocchi hanno lunghezza diversa.
 */
export function costruisciRighe(
  dipendenti: readonly Dipendente[],
  opzioni: { raggruppaPerSede?: boolean } = {},
): RigaGriglia[] {
  const righe: RigaGriglia[] = [];
  let gruppoCorrente: string | null = null;

  for (const dipendente of dipendenti) {
    if (opzioni.raggruppaPerSede) {
      const gruppo = `${dipendente.sedeNome} — ${dipendente.repartoNome}`;
      if (gruppo !== gruppoCorrente) {
        gruppoCorrente = gruppo;
        righe.push({
          chiave: `gruppo:${gruppo}`,
          tipo: 'intestazioneGruppo',
          dipendente: null,
          gruppo,
          primaDelBlocco: true,
          ultimaDelBlocco: true,
        });
      }
    }

    // Tre righe per tutti, funzionari compresi.
    //
    // I funzionari ne avevano una quarta, azzurra, con il programma
    // settimanale. Era sola lettura di un dato che nell'applicazione non si
    // puo' scrivere - arriva solo dall'importazione del file Excel - e lo
    // stesso dato si legge meglio nella pagina Funzionari, che e' fatta
    // apposta. Nelil calendario occupava una riga su quattro per dodici persone
    // senza rispondere a nessuna domanda che il calendario pone.
    const tipi: TipoRiga[] = ['evento', 'ore', 'nota'];

    tipi.forEach((tipo, indice) => {
      righe.push({
        chiave: `${dipendente.id}:${tipo}`,
        tipo,
        dipendente,
        gruppo: null,
        primaDelBlocco: indice === 0,
        ultimaDelBlocco: indice === tipi.length - 1,
      });
    });
  }

  return righe;
}

/** Chiave di indicizzazione di una cella. */
function chiaveCella(dipendenteId: string, data: DataISO): string {
  return `${dipendenteId}|${data}`;
}

export type IndiceEventi = ReadonlyMap<string, Evento[]>;
export type IndiceProgrammi = ReadonlyMap<string, string>;

/**
 * Cosa non puo' coesistere con cosa nello stesso giorno.
 *
 * Rispecchia la colonna generata `eventi.categoria_conflitto` nel database:
 * le assenze si escludono fra loro, la trasferta e la nota convivono con
 * un'assenza. Le due definizioni devono restare allineate — se divergono,
 * l'interfaccia lascia comporre una richiesta che il database poi rifiuta.
 */
export type CategoriaConflitto = 'ASSENZA' | 'SPOSTAMENTO' | 'NOTA';

export function categoriaConflitto(tipo: string): CategoriaConflitto {
  if (tipo === 'SPOSTAMENTO') return 'SPOSTAMENTO';
  if (tipo === 'NOTA') return 'NOTA';
  return 'ASSENZA';
}

/**
 * Espande gli eventi (che sono intervalli) in una mappa giorno per giorno.
 *
 * L'API restituisce dati sparsi — circa 3.250 eventi per un anno intero —
 * invece di una griglia densa da 67.000 celle quasi tutte vuote. L'espansione
 * avviene una volta sola qui, e da quel momento ogni cella si risolve con una
 * ricerca in tempo costante: e' cio' che permette di ridisegnare durante lo
 * scorrimento senza scandire l'elenco degli eventi a ogni cella.
 *
 * Le date si incrementano come stringhe ISO tramite UTC, mai con l'ora
 * locale: un `new Date('2026-10-25')` sommato di un giorno nel fuso sbagliato
 * puo' restare fermo o saltarne due nella notte del cambio ora.
 */
export function indicizzaEventi(eventi: readonly Evento[]): IndiceEventi {
  const indice = new Map<string, Evento[]>();

  for (const evento of eventi) {
    // Annullata e rifiutata escono entrambe dalla griglia: in nessuno dei due
    // casi la persona e' assente, e mostrarle colorerebbe un giorno in cui
    // invece sara' al lavoro. Sono gli stessi stati che il vincolo di
    // sovrapposizione nel database considera liberi.
    if (evento.stato === 'ANNULLATO' || evento.stato === 'RIFIUTATO') continue;
    for (const giorno of giorniDellIntervallo(evento.dataInizio, evento.dataFine)) {
      const chiave = chiaveCella(evento.dipendenteId, giorno);
      const presenti = indice.get(chiave);
      if (presenti) presenti.push(evento);
      else indice.set(chiave, [evento]);
    }
  }

  // Ordine stabile: prima l'assenza, poi la trasferta, poi la nota. Senza,
  // la cella mostrerebbe "TRASFERTA + ORE FERIE" o "ORE FERIE + TRASFERTA" a
  // seconda dell'ordine di arrivo dall'API, che cambia senza preavviso.
  const ordine: Record<CategoriaConflitto, number> = { ASSENZA: 0, SPOSTAMENTO: 1, NOTA: 2 };
  for (const lista of indice.values()) {
    lista.sort((a, b) => ordine[categoriaConflitto(a.tipo)] - ordine[categoriaConflitto(b.tipo)]);
  }

  return indice;
}

export function indicizzaProgrammi(
  programmi: readonly ProgrammaSettimanale[],
): IndiceProgrammi {
  const indice = new Map<string, string>();
  for (const p of programmi) {
    indice.set(chiaveCella(p.dipendenteId, p.data), p.testo);
  }
  return indice;
}

/** Tutti gli eventi di una cella, in ordine stabile. Vuoto se non ce ne sono. */
export function eventiDi(
  indice: IndiceEventi,
  dipendenteId: string,
  data: DataISO,
): readonly Evento[] {
  return indice.get(chiaveCella(dipendenteId, data)) ?? [];
}

/**
 * L'assenza della cella, se c'e'.
 *
 * E' quella che determina il colore verde dell'approvazione e che impedisce
 * di inserirne un'altra: una trasferta accanto non cambia nessuna delle due
 * cose.
 */
export function assenzaDi(
  indice: IndiceEventi,
  dipendenteId: string,
  data: DataISO,
): Evento | undefined {
  return eventiDi(indice, dipendenteId, data).find(
    (e) => categoriaConflitto(e.tipo) === 'ASSENZA',
  );
}

export function programmaDi(
  indice: IndiceProgrammi,
  dipendenteId: string,
  data: DataISO,
): string | undefined {
  return indice.get(chiaveCella(dipendenteId, data));
}

/** Tutte le date fra due estremi inclusi, come stringhe ISO. */
export function giorniDellIntervallo(dal: DataISO, al: DataISO): DataISO[] {
  const giorni: DataISO[] = [];
  const fine = Date.parse(`${al}T00:00:00Z`);
  let corrente = Date.parse(`${dal}T00:00:00Z`);

  if (Number.isNaN(corrente) || Number.isNaN(fine)) return giorni;

  while (corrente <= fine) {
    giorni.push(new Date(corrente).toISOString().slice(0, 10));
    corrente += 86_400_000;
  }
  return giorni;
}

/**
 * Cosa scrivere nella cella, riga per riga.
 *
 * Concentrare qui la scelta del testo evita che il componente si riempia di
 * condizioni annidate, e rende verificabile la regola con un test invece che
 * con un'ispezione a occhio del DOM.
 */
export function contenutoCella(riga: RigaGriglia, eventi: readonly Evento[]): string {
  switch (riga.tipo) {
    case 'evento':
      // Una giornata puo' avere un'assenza E una trasferta: si mostrano
      // entrambe, altrimenti una delle due sparirebbe dalla griglia proprio
      // nel caso che abbiamo appena reso possibile.
      return eventi.map((e) => e.etichetta).join(' + ');
    case 'ore': {
      // `4` diventa "4 ore", `0.5` diventa "0,5 ore": nel file le ore erano
      // testo libero ed e' cosi' che le scrivevano le persone.
      const conOre = eventi.find((e) => e.ore != null);
      return conOre?.ore != null ? `${formattaOre(conOre.ore)} ore` : '';
    }
    case 'nota':
      // La destinazione di una trasferta e la nota di un permesso sono due
      // informazioni diverse che possono coesistere sullo stesso giorno.
      return eventi
        .map((e) => e.nota)
        .filter((n): n is string => n != null && n !== '')
        .join(' · ');
    case 'intestazioneGruppo':
      return '';
  }
}

export function formattaOre(ore: number): string {
  return String(ore).replace('.', ',');
}
